import { create } from 'zustand'
import { persist } from 'zustand/middleware'

export interface Hospital {
  id: string
  name: string
  type: string
  postalCode: string
  address: string
  tel: string
  email: string
  representative: string
  status: 'active' | 'inactive'
  createdAt: string
  updatedAt: string
}

interface HospitalState {
  hospitals: Hospital[]
  addHospital: (hospital: Omit<Hospital, 'id' | 'createdAt' | 'updatedAt'>) => string
  getHospital: (id: string) => Hospital | undefined
  updateHospital: (id: string, data: Partial<Hospital>) => void
  deleteHospital: (id: string) => void
}

const initialHospitals: Hospital[] = [
  {
    id: 'hosp-001',
    name: 'Baseクリニック赤坂',
    type: 'クリニック',
    postalCode: '107-0052',
    address: '東京都港区赤坂2-14-27 国際新赤坂ビル東館2F',
    tel: '03-1234-5678',
    email: 'info@base-clinic.jp',
    representative: '吉岡 紘平',
    status: 'active',
    createdAt: '2020-04-01',
    updatedAt: '2024-01-15',
  },
  {
    id: 'hosp-002',
    name: 'サンテクリニック',
    type: 'クリニック',
    postalCode: '150-0001',
    address: '東京都渋谷区神宮前1-2-3',
    tel: '03-2345-6789',
    email: 'info@sante-clinic.jp',
    representative: '山田 太郎',
    status: 'active',
    createdAt: '2019-06-15',
    updatedAt: '2024-02-20',
  },
  {
    id: 'hosp-003',
    name: '城谷バイオウェルネスクリニック',
    type: 'クリニック',
    postalCode: '104-0061',
    address: '東京都中央区銀座5-10-1',
    tel: '03-3456-7890',
    email: 'info@shirotani-clinic.jp',
    representative: '城谷 昌彦',
    status: 'active',
    createdAt: '2018-03-10',
    updatedAt: '2024-03-05',
  },
  {
    id: 'hosp-004',
    name: '二子玉川メディカルクリニック',
    type: 'クリニック',
    postalCode: '158-0094',
    address: '東京都世田谷区玉川3-6-1',
    tel: '03-4567-8901',
    email: 'info@futako-medical.jp',
    representative: '佐藤 健一',
    status: 'active',
    createdAt: '2021-01-20',
    updatedAt: '2024-01-10',
  },
  {
    id: 'hosp-005',
    name: '感謝クリニック',
    type: 'クリニック',
    postalCode: '530-0001',
    address: '大阪府大阪市北区梅田1-1-1',
    tel: '06-1234-5678',
    email: 'info@kansha-clinic.jp',
    representative: '田村 一郎',
    status: 'active',
    createdAt: '2020-08-01',
    updatedAt: '2024-02-28',
  },
  {
    id: 'hosp-006',
    name: 'LAGOMクリニック',
    type: 'クリニック',
    postalCode: '460-0008',
    address: '愛知県名古屋市中区栄3-4-5',
    tel: '052-234-5678',
    email: 'info@lagom-clinic.jp',
    representative: '鈴木 花子',
    status: 'active',
    createdAt: '2022-04-15',
    updatedAt: '2024-03-01',
  },
  {
    id: 'hosp-007',
    name: 'よろずクリニック',
    type: 'クリニック',
    postalCode: '812-0011',
    address: '福岡県福岡市博多区博多駅前2-1-1',
    tel: '092-345-6789',
    email: 'info@yorozu-clinic.jp',
    representative: '高橋 誠',
    status: 'active',
    createdAt: '2021-09-01',
    updatedAt: '2024-02-15',
  },
  {
    id: 'hosp-008',
    name: 'シンバイオシス研究所',
    type: '研究機関',
    postalCode: '113-0033',
    address: '東京都文京区本郷7-3-1',
    tel: '03-5678-9012',
    email: 'info@symbiosis.jp',
    representative: '中川 直哉',
    status: 'active',
    createdAt: '2017-10-01',
    updatedAt: '2024-03-10',
  },
  {
    id: 'hosp-009',
    name: 'FMT事務局',
    type: '事務局',
    postalCode: '100-0001',
    address: '東京都千代田区千代田1-1',
    tel: '03-0000-0000',
    email: 'office@fmt-japan.org',
    representative: '田中三紀子',
    status: 'active',
    createdAt: '2015-01-01',
    updatedAt: '2024-01-01',
  },
]

export const useHospitalStore = create<HospitalState>()(
  persist(
    (set, get) => ({
      hospitals: initialHospitals,

      addHospital: (hospitalData) => {
        const id = `hosp-${Date.now()}`
        const now = new Date().toISOString().split('T')[0]
        const newHospital: Hospital = {
          ...hospitalData,
          id,
          createdAt: now,
          updatedAt: now,
        }

        set((state) => ({
          hospitals: [newHospital, ...state.hospitals],
        }))

        return id
      },

      getHospital: (id: string) => {
        return get().hospitals.find((h) => h.id === id)
      },

      updateHospital: (id: string, data: Partial<Hospital>) => {
        set((state) => ({
          hospitals: state.hospitals.map((hospital) =>
            hospital.id === id
              ? { ...hospital, ...data, updatedAt: new Date().toISOString().split('T')[0] }
              : hospital
          ),
        }))
      },

      deleteHospital: (id: string) => {
        set((state) => ({
          hospitals: state.hospitals.filter((hospital) => hospital.id !== id),
        }))
      },
    }),
    {
      name: 'fmt-japan-hospitals',
    }
  )
)



